import {
  Armchair,
  Users,
  CalendarHeart,
  HeartPulse,
  Sparkles,
  Repeat,
  Factory,
  Stethoscope,
  Fuel,
  Plane,
  Building2,
  Briefcase,
} from "lucide-react";
import { IMAGES } from "@/config/site";
import { CORPORATE_PROGRAMS, CORPORATE_INDUSTRIES } from "@/config/services";
import { useSeo } from "@/lib/seo";
import { PageHero } from "@/components/common/PageHero";
import { Section, SectionHeading } from "@/components/common/Section";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/common/Reveal";
import { CorporateCard } from "@/components/common/CorporateCard";
import { ProtoForm } from "@/components/forms/ProtoForm";
import { corporateFields } from "@/components/forms/formSchemas";
import { CollectiveEndorsement } from "@/components/common/CollectiveEndorsement";

const PROGRAM_ICONS = {
  "employee-wellness": Users,
  "appreciation-events": CalendarHeart,
  "recurring-programs": Repeat,
  "health-fairs": HeartPulse,
  "workplace-activations": Sparkles,
};

const INDUSTRY_ICONS = {
  Manufacturing: Factory,
  Healthcare: Stethoscope,
  "Oil & Gas": Fuel,
  Aviation: Plane,
  "Large Employers & Corporate Offices": Building2,
};

const WHY_WORK = [
  { icon: Armchair, title: "On-site & low-friction", text: "Chair and table massage that fits into the workday without disrupting operations." },
  { icon: Users, title: "Team appreciation", text: "A meaningful way to recognize employees and strengthen workplace culture." },
  { icon: Repeat, title: "Flexible cadence", text: "One-time events or recurring programs, scaled to your headcount and goals." },
];

export default function CorporateWellness() {
  useSeo({
    title: "Corporate Wellness & Workplace Massage Houston | Legacy Spa",
    description:
      "Corporate chair and table massage, employee-appreciation events, health fairs and recurring workplace-wellness programs in Houston. Request a corporate consultation.",
    path: "/corporate-wellness",
  });

  return (
    <>
      <PageHero
        eyebrow="Corporate Wellness"
        titleLines={["Wellness that", "works at work."]}
        lead="On-site massage and workplace-wellness programs designed for Houston employers who invest in their people."
        image={IMAGES.office}
        imageAlt="A modern corporate office environment."
      />

      {/* B2B light band */}
      <section className="bg-legacy-ivory py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            eyebrow="Confirmed Offerings"
            title="Programs built for your team."
            tone="light"
            as="h2"
            lead="Chair massage, on-site table massage, employee-appreciation events, health & wellness events, and recurring workplace programs."
          />
          <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CORPORATE_PROGRAMS.map((p, i) => (
              <StaggerItem key={p.id}>
                <CorporateCard program={p} index={i} icon={PROGRAM_ICONS[p.id]} />
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Why massage at work */}
      <Section className="bg-legacy-charcoal">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src={IMAGES.corporateChair}
                alt="Employee receiving an on-site chair massage in the workplace."
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow="Why Massage at Work" title="A calmer, more focused team." as="h2" />
            <div className="mt-8 space-y-5">
              {WHY_WORK.map((w) => (
                <Reveal key={w.title}>
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-legacy-mint/10 text-legacy-mint">
                      <w.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-heading text-xl text-white">{w.title}</h3>
                      <p className="mt-1 text-sm text-white/60">{w.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-8 text-sm text-white/45">
              Legacy Spa does not claim guaranteed health outcomes, productivity
              gains or return on investment. Programs are tailored per
              organization.
            </p>
          </div>
        </div>
      </Section>

      {/* Priority industries */}
      <Section className="bg-[#12201d]">
        <SectionHeading eyebrow="Priority Industries" title="Serving Houston's key sectors." as="h2" />
        <StaggerGroup className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {CORPORATE_INDUSTRIES.map((ind) => {
            const Icon = INDUSTRY_ICONS[ind] || Briefcase;
            return (
              <StaggerItem key={ind}>
                <div className="flex h-full flex-col items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                  <Icon className="h-7 w-7 text-legacy-mint" aria-hidden="true" />
                  <span className="font-medium text-white/85">{ind}</span>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </Section>

      {/* Consultation form */}
      <section id="consultation" className="scroll-mt-24 bg-legacy-ivory py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionHeading
                eyebrow="Let's Talk"
                title="Request a corporate consultation."
                tone="light"
                as="h2"
                lead="Tell us about your team and goals. We'll follow up to design the right program."
              />
              <CollectiveEndorsement tone="light" className="mt-10" />
            </div>
            <div className="lg:col-span-8">
              <ProtoForm
                formId="corporate-consultation"
                endpoint="corporate"
                smsOptIn
                testId="corporate-lead-form"
                tone="light"
                fields={corporateFields}
                submitLabel="Request Consultation"
                consentLabel="I consent to Legacy Spa contacting me about this corporate inquiry. (No employee health or medical information should be submitted.)"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
